/** @jsx tsx */
import { tsx } from "@dojo/framework/core/vdom";
import { WidgetBase } from "@dojo/framework/core/WidgetBase";
import { SELECTION_HANDLER_STROKE_WIDTH } from "../../Constants";
import { IScaleObjects } from "../../store/ApiContracts";
import { PageObjectScalePointType } from "../interfaces/model/enumerations/PageObjectScalePoints";
import { PageObjectType } from "../interfaces/model/Types";
import { IShape } from "../interfaces/model/PageObjects/Shape";
import { cancelCurrentOperation, isExpandFromCenter, maintainAspectRatio } from "../keyUtil";
import { IPosition } from "../meta/Drag/CsiDragController";
import { CsiDrag, IDragResults } from "../meta/Drag/index";
import { getZoomValue } from "../selectors/index";
import { isCornerScalePoint } from "../../store/reducers/util";
import { AutosizeMode } from "../interfaces/model/enumerations/index";
import { IGroupShape } from "../interfaces/model/PageObjects";
import { CURSOR_CSS_MAP } from "../../Constants";
import { adjustPositionForRoot } from "../util";

interface IShapeBox {
    x: number;
    y: number;
    width: number;
    height: number;
}

interface ISelectionBoxPointProps extends IShapeBox {
    canvasContainerEl: HTMLElement;
    css: string;
    stroke: string;
    locked: boolean;
    scalePointType: PageObjectScalePointType;
    groupObject: PageObjectType | undefined;
    pageObject: PageObjectType;

    /**
     * Flag indicating whether the canvas is in panning mode or not
     */
    isPanning: boolean;

    /**
     * Flag indicating whether the shape is temporary (for drawing)
     */
    isTempShape: boolean;

    /**
     * Position in canvas.
     */
    positionInCanvas: IPosition;

    /**
     * Scale object callback
     */
    scaleObjects: IScaleObjects;

    /**
     * Set Autosize callback
     * @param autosize { AutosizeMode } - Autosize Mode
     */
    setShapeAutosizeValues(autosize: AutosizeMode, resizeForContent: boolean): void;

    /**
     * Set all aspect ratios callback
     */
    setAllAspectRatios(transient: boolean): void;
};

export class SelectionBoxPoint extends WidgetBase<ISelectionBoxPointProps> {
	/**
     * Flag to indicate if dragging is started or not
     */
	private _dragging: boolean = false;

	/**
     * Style to use on cursor
     */
	private cursorStyle = "";

	/**
     * Flag to represents whether escape key is press while dragging
     */
	private isEscapeKeyPress: boolean | undefined = false;

	/**
     * Remove all pointer event listeners on shape
     */
	protected onDetach(): void {
		this.meta(CsiDrag)
			.remove(this.properties.key as string);
	}

	protected _onPointerDrag(drag: IDragResults) {
		// if dragging is not yet started return
		if (!this._dragging && !drag.isDragging) {
			return;
		}

		if (this._dragging && !drag.isDragging) {
			// if previously dragging was true and now dragResults say it is false then dragging is stopped.
			this._dragging = false;
		} else if (!drag.delta.x && !drag.delta.y) {
			return;
		} else {
			this._dragging = true;
		}

		// if dragging is continued we want to keep these changes as transient.
		const isTransient = this._dragging;
		const pageObject = this.properties.pageObject as IShape;
		const groupObject = this.properties.groupObject as IGroupShape;
		const scalePointType = this.properties.scalePointType;
		const setAutosize = this.properties.setShapeAutosizeValues;
		let isMaintainAspectRatio = maintainAspectRatio(drag.keyData) || pageObject.noChangeAspect;
		if (pageObject.isPicture && isCornerScalePoint(scalePointType)) {
			isMaintainAspectRatio = true;
		} else if ((pageObject.isAttributePlaceholder || pageObject.isTextShape) && pageObject.spPr.autosize !== AutosizeMode.FIXED_SIZE) {
			if (scalePointType === PageObjectScalePointType.SIDE_LEFT ||
                scalePointType === PageObjectScalePointType.SIDE_RIGHT) {
				setAutosize(AutosizeMode.FIXED_WIDTH, false);
			} else {
				setAutosize(AutosizeMode.FIXED_SIZE, false);
			}
		}
		this.isEscapeKeyPress = cancelCurrentOperation(drag.keyData);
		const expandFromCenter = isExpandFromCenter(drag.keyData);
		const currentPosition = drag.positionInCanvas as IPosition;
		const delta = {
			x: drag.positionInCanvas.x - this.properties.positionInCanvas.x,
			y: drag.positionInCanvas.y - this.properties.positionInCanvas.y,
		};
		adjustPositionForRoot(this.properties.canvasContainerEl, delta);
		this.properties.scaleObjects(scalePointType, delta, currentPosition,
			expandFromCenter || false, isMaintainAspectRatio || false, pageObject.shapeId,
			null, isTransient || this.isEscapeKeyPress, false,
			groupObject ? groupObject.shapeId : undefined);

		if (!(isTransient || this.isEscapeKeyPress)) {
			this.properties.setAllAspectRatios(isTransient);
		}
	}

	/**
     * on pointer up event for selection box point
     * @param {PointerEvent} e
     */
	protected _onPointerDown(e) {
		const {
			canvasContainerEl,
			isPanning,
		} = this.properties;
		if (canvasContainerEl && !isPanning) {
			canvasContainerEl.style.cursor = CURSOR_CSS_MAP[this.cursorStyle] || "";
		}
	}

	protected render() {
		const {
			css,
			isPanning,
			height,
			isTempShape,
			key,
			locked,
			positionInCanvas,
			stroke,
			width,
			x,
			y,
		} = this.properties;
		this.cursorStyle = css;
		const dragResult: IDragResults = this.meta(CsiDrag)
			.get(key as string);
		this._onPointerDrag(dragResult);
		return (
			<g>
				<rect
					classes={isTempShape || isPanning || locked ? "" : `${css} c8_canvas_userSelectNone`}
					data-position={JSON.stringify(positionInCanvas)}
					fill="white"
					height={`${height}`}
					key={key}
					onpointerdown={this._onPointerDown}
					pointer-events="visible"
					stroke={`${stroke}`}
					stroke-width={`${getZoomValue(SELECTION_HANDLER_STROKE_WIDTH)}`}
					width={`${width}`}
					x={`${x}`}
					y={`${y}`}
				/>
			</g>
		);
	}
}
